/**
 * Tenant detection helpers.
 *
 * DYMAXION runs tenant-isolated spokes at `<tenant>.dymaxion.com.au`
 * and per-host VPS instances at `archon-host<N>.dymaxion.com.au`. The
 * canonical short names align with the production tenant-config.json
 * `spokeName` field on each host:
 *
 *   dymaxion-dev   → VPS dev (archon-host1.dymaxion.com.au)
 *   glnty1         → Gallantree spoke
 *   tpg1           → TPG spoke
 *
 * The list of known tenants is supplied at runtime from
 * /config.json — see runtimeConfig.ts. The hard-coded fallback list
 * here is only used by unit tests and when detectTenant is called
 * before the runtime config has loaded.
 */
const FALLBACK_KNOWN_TENANTS = ['dymaxion-dev', 'glnty1', 'tpg1'];
/**
 * Detect the tenant environment from the current hostname.
 *
 * The optional `knownTenants` argument should be the list from the
 * runtime config so this function honours per-deployment tenant
 * registration. Falls back to the compiled-in list if not provided.
 */
export function detectTenant(hostname = typeof window !== 'undefined' ? window.location.hostname : '', knownTenants = FALLBACK_KNOWN_TENANTS) {
    const host = hostname.toLowerCase();
    const known = new Set(knownTenants.map((t) => t.toLowerCase()));
    if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')) {
        return { kind: 'local', host };
    }
    // archon-host1.dymaxion.com.au, archon-host2.dymaxion.com.au, etc.
    const vpsMatch = host.match(/^archon-host(\d+)\.dymaxion\.com\.au$/);
    if (vpsMatch) {
        return { kind: 'vps-dev', host, spokeName: 'dymaxion-dev' };
    }
    if (host === 'archon.dymaxion.com.au') {
        return { kind: 'vps-dev', host, spokeName: 'dymaxion-dev' };
    }
    if (host === 'dymaxion.com.au' || host === 'www.dymaxion.com.au') {
        return { kind: 'public', host };
    }
    // <tenant>.dymaxion.com.au — match against the runtime tenant list
    const tenantMatch = host.match(/^([a-z0-9-]+)\.(?:archon-bmad|dymaxion)\.com(?:\.au)?$/);
    if (tenantMatch?.[1] && known.has(tenantMatch[1])) {
        return { kind: 'spoke', host, spokeName: tenantMatch[1] };
    }
    // Hostnames like archon-tpg1.dymaxion.com.au — extract the prefix
    const prefixMatch = host.match(/^archon-([a-z0-9-]+)\.dymaxion\.com\.au$/);
    if (prefixMatch?.[1] && known.has(prefixMatch[1])) {
        return { kind: 'spoke', host, spokeName: prefixMatch[1] };
    }
    return { kind: 'unknown', host };
}
/**
 * Convenience: extract the spoke name (or `null` for public/unknown).
 * `dymaxion-dev` for the VPS, the canonical short name (e.g. `glnty1`,
 * `tpg1`) for spokes.
 */
export function getTenantFromHostname(hostname, knownTenants) {
    const env = detectTenant(hostname, knownTenants);
    if (env.kind === 'spoke' || env.kind === 'vps-dev')
        return env.spokeName;
    if (env.kind === 'local')
        return 'dymaxion-dev';
    return null;
}
//# sourceMappingURL=tenant.js.map